import React, { useMemo } from 'react'
import { StayForLong } from '@s4l/stayforlong'
import { IntlProvider, useIntl } from 'react-intl'
import { Device, TypedIntlShape } from '../types'

type AppMobileVersionType = string | null

export interface IStayForLongContext {
  domain: StayForLong
  i18n: TypedIntlShape
  device: Device
  mobileAppVersion: AppMobileVersionType
}

export interface StayForLongProviderProps {
  children: React.ReactNode
  locale: string
  messages: Record<string, string>
  device?: Device
  domain?: StayForLong
  mobileAppVersion?: AppMobileVersionType
}

export const StayForLongContext = React.createContext<IStayForLongContext>({
  domain: StayForLong.empty(),
  i18n: {} as TypedIntlShape,
  device: 'mobile',
  mobileAppVersion: null,
})

const StayForLongProviderInternal = ({
  children,
  domain,
  device,
  mobileAppVersion,
}: {
  children: React.ReactNode
  domain: StayForLong
  device: Device
  mobileAppVersion: AppMobileVersionType
}) => {
  const intl = useIntl() as unknown as TypedIntlShape

  const contextValue = useMemo(
    () => ({ domain, i18n: intl, device, mobileAppVersion }),
    [domain, intl, device, mobileAppVersion]
  )

  return <StayForLongContext.Provider value={contextValue}>{children}</StayForLongContext.Provider>
}

export const StayForLongProvider = ({
  children,
  locale,
  messages,
  device = 'mobile',
  domain = StayForLong.empty(),
  mobileAppVersion = null,
}: StayForLongProviderProps) => {
  const translations =
    typeof window !== 'undefined' && window?.__NEXT_DATA__?.props?.pageProps?.translations
      ? window?.__NEXT_DATA__?.props?.pageProps?.translations
      : messages

  if (!translations) {
    throw new Error('Translations messages are not provided either by window or by ssr parameter')
  }
  return (
    <IntlProvider locale={locale} messages={translations}>
      <StayForLongProviderInternal
        domain={domain}
        device={device}
        mobileAppVersion={mobileAppVersion}
      >
        {children}
      </StayForLongProviderInternal>
    </IntlProvider>
  )
}

export const useS4LContext = () => React.useContext<IStayForLongContext>(StayForLongContext)
export * from './promo'
